<template>
  <div class="hcl-toolbar-content-pager">
    <div
      v-if="paginationItems.length > 0"
      class="pagination-goto"
    >
      <span class="goto-text">{{ gotoLabel }}</span>
      <input
        class="goto-input form-control form-control-sm"
        v-model="gotoTargetPageNumber"
        @keyup.enter="gotoNavigateToTargetPage"
      >
    </div>
    <ul class="pagination">
      <li
        v-for="(item, index) in paginationItems"
        :key="index"
        class="page-item"
        v-bind:class="item.css"
        :data-page="item.page"
      >
        <a
          class="page-link"
          v-if="item.type === 'start'"
          @click="NavPage(item.page)"
        >
          <span
            v-if="item.screenreader"
            class="sr-only"
          >
            <i class="eii--fa6-solid--arrow-left"></i>
          </span>
          <i class="eii--fa6-solid--arrow-left"></i>
        </a>
        <a
          class="page-link"
          v-if="item.type === 'link'"
          @click="NavPage(item.page)"
        >
          <span
            v-if="item.screenreader"
            class="sr-only"
          >{{ item.page }}</span>
          {{ item.page }}
        </a>
        <a
          class="page-link"
          v-if="item.type === 'ellipsis'"
          @click="NavPage(item.page)"
        >
          <span
            v-if="item.screenreader"
            class="sr-only"
          >"...</span>
          ...
        </a>
        <a
          class="page-link"
          v-if="item.type === 'end'"
          @click="NavPage(item.page)"
        >
          <span
            v-if="item.screenreader"
            class="sr-only"
          >
            <i class="eii--fa6-solid--arrow-right"></i>
          </span>
          <i class="eii--fa6-solid--arrow-right"></i>
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick } from 'vue'

import { useIO } from '@/composables/io'

import { DefinitionToolbarContentPager, DataToolbar, DataPaginationItem } from '@/components/types'

import { useDataStore } from '@/stores/data'

import { useActionLists } from '../ToolbarContentActionSelect/actionlists'

const { get } = useDataStore()

interface Props {
  /**
   * Definition as an JS object.
   */
  definition: DefinitionToolbarContentPager
  /**
   * Definition as an JS object.
   */
  data: DataToolbar
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  data: undefined
})

const blockMetaId: string = inject('blockMetaId', '')

const browserJumpToTop = () => {
  const y = window.scrollY
  if (y > 0) {
    window.scrollTo(0, 0)
  }
}

const actionlist = useActionLists()

const updateListSelection = (listMetaId: string, columnIdentity: string, targetState: string) => {
  const bucketList = get(listMetaId)

  if (Object.keys(bucketList).length === 0) return

  actionlist.updateBucketEntitySelection(
    bucketList,
    listMetaId,
    columnIdentity,
    {
      companyState: targetState,
      projectState: targetState,
      contactState: targetState,
      applicantState: targetState,
      employeeState: targetState
    }
  )
}

const { performPostBucket } = useIO()

const navPageCallback = () => {
  nextTick(() => {
    browserJumpToTop()
  })
}

const NavPage = (page: number) => {
  const pageStart = (page - 1) * props.data.pager.pager.rowsPerPage
  const pageEnd = page * props.data.pager.pager.rowsPerPage - 1

  // @improvement: don't define the ajax endpoint in the definition, use the url from the first search result (response.url)!!
  const url = props.data.pager.pagination.ajax

  const data = {
    source: {
      id: props.data.pager.parentid
    },
    pagination: {
      rowsPerPage: props.data.pager.pager.rowsPerPage,
      page: page,
      pageStart: pageStart,
      pageEnd: pageEnd
    },
    sorting: {
      sortColumn: props.data.pager.sorting.sortColumn,
      sortDirection: props.data.pager.sorting.sortDirection
    },
    values: props.data.pager.pager.criteria
  }

  performPostBucket(
    url,
    {},
    data,
    blockMetaId,
    () => {
      navPageCallback()
    }
  )
}

const range = (start: number, end: number) => {
  return [...Array(end - start + 1).keys()].map((i) => i + start)
}

const paginationItems = computed(() => {
  const result: DataPaginationItem[] = []

  if (props.data.pager.pager.pageMax <= 4) {
    for (let page of range(1, props.data.pager.pager.pageMax)) {
      result.push({
        type: 'link',
        screenreader: false,
        css: [props.data.pager.pager.pageCurrent === page ? 'active' : ''],
        page: page
      })
    }

    return result
  }

  if (props.data.pager.pager.pageCurrent >= 3 && props.data.pager.pager.pageCurrent + 2 <= props.data.pager.pager.pageMax) {
    result.push({
      type: 'start',
      screenreader: false,
      css: [props.data.pager.pager.pageCurrent - 1 < 1 ? 'disabled' : ''],
      page: props.data.pager.pager.pageCurrent - 1
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: 1
    })

    result.push({
      type: 'ellipsis',
      screenreader: false,
      css: ['disabled'],
      page: props.data.pager.pager.pageCurrent
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageCurrent - 1
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: ['active'],
      page: props.data.pager.pager.pageCurrent
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageCurrent + 1
    })

    result.push({
      type: 'ellipsis',
      screenreader: false,
      css: ['disabled'],
      page: props.data.pager.pager.pageCurrent
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageMax
    })

    result.push({
      type: 'end',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageCurrent + 1
    })

    return result
  }

  if (props.data.pager.pager.pageCurrent < 3) {
    result.push({
      type: 'start',
      screenreader: false,
      css: [props.data.pager.pager.pageCurrent - 1 < 1 ? 'disabled' : ''],
      page: props.data.pager.pager.pageCurrent - 1
    })

    for (let page of range(1, props.data.pager.pager.pageCurrent + 1)) {
      result.push({
        type: 'link',
        screenreader: false,
        css: [page === props.data.pager.pager.pageCurrent ? 'active' : ''],
        page: page
      })
    }

    result.push({
      type: 'ellipsis',
      screenreader: false,
      css: ['disabled'],
      page: props.data.pager.pager.pageCurrent
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageMax - 1
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageMax
    })

    result.push({
      type: 'end',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageCurrent + 1
    })

    return result
  }

  if (props.data.pager.pager.pageCurrent > props.data.pager.pager.pageMax - 2) {
    result.push({
      type: 'start',
      screenreader: false,
      css: [],
      page: props.data.pager.pager.pageCurrent - 1
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: 1
    })

    result.push({
      type: 'link',
      screenreader: false,
      css: [],
      page: 2
    })

    result.push({
      type: 'ellipsis',
      screenreader: false,
      css: ['disabled'],
      page: props.data.pager.pager.pageCurrent
    })

    for (let page of range(props.data.pager.pager.pageCurrent - 1, props.data.pager.pager.pageMax)) {
      result.push({
        type: 'link',
        screenreader: false,
        css: [page === props.data.pager.pager.pageCurrent ? 'active' : ''],
        page: page
      })
    }

    result.push({
      type: 'end',
      screenreader: false,
      css: [props.data.pager.pager.pageCurrent === props.data.pager.pager.pageMax ? 'disabled' : ''],
      page: props.data.pager.pager.pageCurrent + 1
    })

    return result
  }

  return result
})

const gotoTargetPageNumber = ref('')

const gotoLabel = computed(() => {
  return props.data.pager?.pagination?.placeholder_goto_label ?? 'Zur Seite:'
})

const gotoNavigateToTargetPage = () => {
  const p = parseInt(gotoTargetPageNumber.value, 10)

  if (Number.isNaN(p) || p < props.data.pager.pager.pageMin || p > props.data.pager.pager.pageMax) return

  gotoTargetPageNumber.value = ''

  NavPage(p)
}
</script>

<style lang="scss" scoped>
.hcl-toolbar-content-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .pagination {
    margin: 0;
  }

  .pagination-goto {
    display: flex;
    align-items: center;
    margin-right: 1.5rem;

    .goto-text {
      margin-right: 0.5rem;
      white-space: nowrap;
    }

    .goto-input {
      width: 3.5rem;
    }
  }
}
</style>
